import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { StarIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../config/api';

const GoogleMark = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09A6.97 6.97 0 0 1 5.48 12c0-.73.13-1.43.36-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      fetch(getApiUrl('/api/admin/google-reviews')).then(r => r.json()).catch(() => []),
      fetch(getApiUrl('/api/products/reviews/recent?all=true')).then(r => r.json()).catch(() => [])
    ])
      .then(([google, store]) => {
        const googleList = (Array.isArray(google) ? google : []).map(item => ({
          rating: item.rating,
          comment: item.comment,
          images: [],
          createdAt: item.reviewDate || item.createdAt,
          user: { name: item.authorName },
          source: 'google',
          reviewId: item._id,
          product: null
        }));
        const storeList = (Array.isArray(store) ? store : []).map(item => ({
          ...item,
          source: 'store'
        }));
        setReviews([...googleList, ...storeList]);
      })
      .catch(() => setError('Failed to load reviews'))
      .finally(() => setLoading(false));
  }, []);

  const average = useMemo(() => {
    if (!reviews.length) return 0;
    return reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length;
  }, [reviews]);

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <Helmet>
        <title>Customer Reviews | theVastra.store</title>
      </Helmet>

      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            <GoogleMark className="w-8 h-8" />
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-pink-900">All Customer Reviews</h1>
          </div>
          <p className="text-gray-600">Google reviews and shopper reviews from theVastra.store</p>
          {!loading && reviews.length > 0 && (
            <p className="mt-3 text-gray-800 font-medium">
              {average.toFixed(1)} / 5 · {reviews.length} review{reviews.length === 1 ? '' : 's'}
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600" />
          </div>
        ) : error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : reviews.length === 0 ? (
          <p className="text-center text-gray-500">No reviews yet. Be the first to review a product after delivery.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <article key={review.reviewId || index} className="bg-white rounded-2xl shadow p-6 border border-gray-100 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIcon
                        key={star}
                        className={`w-5 h-5 ${star <= review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                      />
                    ))}
                  </div>
                  {review.source === 'google' && <GoogleMark />}
                </div>
                <p className="text-gray-700 italic mb-4 flex-1">"{review.comment}"</p>
                {review.images?.length > 0 && (
                  <div className="flex gap-2 mb-4 overflow-x-auto">
                    {review.images.map((image, imgIndex) => (
                      <img key={imgIndex} src={image} alt="" className="w-16 h-16 object-cover rounded-lg flex-shrink-0" />
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-auto">
                  <div className="w-10 h-10 bg-gradient-to-r from-pink-400 to-indigo-400 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {review.user?.name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{review.user?.name || 'Anonymous'}</p>
                    <p className="text-sm text-gray-500">
                      {review.createdAt ? new Date(review.createdAt).toLocaleDateString() : ''}
                    </p>
                  </div>
                </div>
                {review.product?.name && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">Review for</p>
                    {review.product._id ? (
                      <Link to={`/product/${review.product._id}`} className="text-sm font-medium text-pink-700 hover:underline">
                        {review.product.name}
                      </Link>
                    ) : (
                      <p className="text-sm font-medium text-gray-900">{review.product.name}</p>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewsPage;
